// src/components/events/EventManager.jsx
import React, { useState, useEffect } from 'react';
import {
  User,
  MessageSquare, 
  X, 
  Plus, 
  Calendar, 
  Users, 
  Gift, 
  Clock, 
  Eye,
  EyeOff,
  Bell,
  Flag,
  Truck, 
  XCircle,
  CheckCircle,
  Camera,
  Edit,
  Trash2,
  Shield,
  Search,
  Download,
  Upload,
  ClipboardList, 
  PlayCircle,   
  Settings,  
  PauseCircle,    
  Square,         
  ChevronDown,
  AlertCircle,    
  Tag,            
  ArrowRight,
  ArrowLeft,  
  Filter,     
  BarChart3,
  DollarSign,
  Wallet   
} from 'lucide-react';
import { eventsService } from '../../services/eventsService';
import { albumService } from '../../services/albumService';
import { transactionService } from '../../services/transactionService';
import { AddEditModal } from './EventEditModal';
import { ModerationModal } from './ModerationModal';
import { GalleryManager } from '../album/GalleryManager';
import { LayoutRenderer } from '../renderers/LayoutRenderer';
import { MediaCard } from '../album/AlbumMediaCard';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { GallerySidestrip } from '../album/GallerySidestrip';

import { GuestsTab } from './GuestsTab';
import { TransactSection } from '../shared/sections/TransactionSection';

export const EventManager = ({ bookId, book, user, eventSettings, isOpen, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  
  // Data states
  const [bookings, setBookings] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [checkIns, setCheckIns] = useState([]);

  // Modal states  
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [modalType, setModalType] = useState('');

  const [moderationStats, setModerationStats] = useState(null);
  const [moderationItems, setModerationItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [moderationFilter, setModerationFilter] = useState('pending'); // pending, reported, all
  const [moderationSearch, setModerationSearch] = useState('');
  const [moderationLoading, setModerationLoading] = useState(false);

  const [messages, setMessages] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [messageSearch, setMessageSearch] = useState('');
  const [messageFilter, setMessageFilter] = useState('all');

  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);

  const [guests, setGuests] = useState([]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Calendar }, 
    { id: 'guests', label: 'Guest List', icon: Users, count: guests.length },
    { id: 'bookings', label: 'Bookings', icon: Users, count: bookings.length },
    { id: 'tasks', label: 'Tasks', icon: ClipboardList, count: tasks?.length || 0 },
    { id: 'finances', label: 'Finances', icon: DollarSign },
    { id: 'messages-updates', label: 'Messages & Updates', icon: MessageSquare, 
      count: (messages?.length || 0) + (updates?.length || 0) },
      /*
    { id: 'moderation', label: 'Moderation', icon: Shield,
      badge: moderationStats?.totalPending > 0 ? moderationStats.totalPending : null,
      badgeColor: 'bg-orange-500', show: eventSettings?.moderateMessages || eventSettings?.moderateUpdates
      },
      */
    { id: 'milestones', label: 'Milestones', icon: CheckCircle, count: milestones.length },
    { id: 'gifts', label: 'Gift Registry', icon: Gift, count: gifts.length },
    { id: 'suppliers', label: 'Suppliers', icon: Truck, count: suppliers.length },
    { id: 'galleries', label: 'Galleries', icon: Camera, count: galleries.length }
  ];

  useEffect(() => {
    if (isOpen && bookId) {
      loadAllData();
      loadMessages();
      loadTasks();
      loadModerationStats();
    }
  }, [isOpen, bookId]);

  useEffect(() => {
    if (bookId && (eventSettings?.moderateMessages || eventSettings?.moderateUpdates)) {
      loadModerationStats();
    }
  }, [bookId]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [
        bookingsData,
        milestonesData, 
        giftsData,
        suppliersData,
        galleriesData,
        checkInsData,
        analyticsData,
        moderationStats
      ] = await Promise.all([
        eventsService.getEventBookings(bookId),
        eventsService.getEventMilestones(bookId),
        eventsService.getGiftRegistry(bookId),
        eventsService.getEventSuppliers(bookId),
        albumService.getBookGalleries(bookId),
        eventsService.getEventCheckIns(bookId),
        eventsService.getEventAnalytics(bookId),
        eventsService.getModerationStats(bookId)
      ]);

      const guestsData = await eventsService.getEventGuests(bookId); // ADD THIS LINE
      setGuests(guestsData);

      setBookings(bookingsData);
      setMilestones(milestonesData);
      setGifts(giftsData);
      setSuppliers(suppliersData);
      setGalleries(galleriesData);
      setCheckIns(checkInsData);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading event data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    if (!bookId) return;
    setMessagesLoading(true);
    try {
      const messagesData = await eventsService.getEventMessages(bookId);
      const updatesData = await eventsService.getEventUpdates(bookId);
      setMessages(messagesData || []);
      setUpdates(updatesData || []);
    } catch (error) {
      console.error('Error loading messages/updates:', error);
      setMessages([]);
      setUpdates([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  const loadTasks = async () => {
    if (!bookId) return;
    setTasksLoading(true);
    try {
      const tasksData = await eventsService.getEventTasks(bookId);
      setTasks(tasksData || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  };

  const handleAdd = (type) => {
    setModalType(type);
    setEditingItem(null);
    setShowAddModal(true);
  };

  const handleEdit = (item, type) => {
    setModalType(type);
    setEditingItem(item);
    setShowAddModal(true);
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      switch (type) {
        case 'booking':
          await eventsService.deleteBooking(bookId, id);
          break;
        case 'milestone':
          await eventsService.deleteMilestone(bookId, id);
          break;
        case 'gift':
          await eventsService.deleteGiftItem(bookId, id);
          break;
        case 'supplier':
          await eventsService.deleteSupplier(bookId, id);
          break;
      }
      await loadAllData();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item');
    }
  };

  const loadModerationItems = async (filter = moderationFilter) => {
    if (!bookId || !(book.eventSettings?.moderateMessages || book.eventSettings?.moderateUpdates)) {
      return;
    }

    setModerationLoading(true);
    try {
      let items = [];
      
      if (filter === 'pending') {
        items = await eventsService.getModerationQueue(bookId);
      } else if (filter === 'reported') {
        items = await eventsService.getReportedContent(bookId);
      } else {
        items = await eventsService.getAllContent(bookId);
      }

      setModerationItems(items);
    } catch (error) {
      console.error('Error loading moderation items:', error);
      setModerationItems([]);
    } finally {
      setModerationLoading(false);
    }
  };

  // Handle moderation actions
  const handleModerate = async (itemId, action, itemType) => {
    try {
      if (itemType === 'message') {
        await eventsService.moderateMessage(bookId, itemId, action, user.uid);
      } else {
        await eventsService.moderateUpdate(bookId, itemId, action, user.uid);
      }
      
      // Reload moderation data
      await Promise.all([
        loadModerationItems(),
        loadModerationStats()
      ]);
    } catch (error) {
      console.error('Error moderating item:', error);
    }
  };

  const handleBulkModerate = async (action) => {
    if (selectedItems.length === 0) return;

    try {
      const messageIds = selectedItems.filter(item => 
        moderationItems.find(i => i.id === item)?.itemType === 'message'
      );

      if (messageIds.length > 0) {
        await eventsService.bulkModerateMessages(bookId, messageIds, action, User.uid);
      }
      
      // Handle updates individually since we don't have bulk update moderation yet
      const updateIds = selectedItems.filter(item => 
        moderationItems.find(i => i.id === item)?.itemType === 'update'
      );
      
      for (const updateId of updateIds) {
        await eventsService.moderateUpdate(bookId, updateId, action, 'current_user_id');
      }
      
      setSelectedItems([]);
      await Promise.all([
        loadModerationItems(),
        loadModerationStats()
      ]);
    } catch (error) {
      console.error('Error bulk moderating:', error);
    }
  };

  const loadModerationStats = async () => {
    if (!bookId || !(book.eventSettings?.moderateMessages || book.eventSettings?.moderateUpdates)) {
      return;
    }

    try {
      const stats = await eventsService.getModerationStats(bookId);
      setModerationStats(stats);
    } catch (error) {
      console.error('Error loading moderation stats:', error);
    }
  };

  useEffect(() => {
    loadAllData();
  }, [bookId]);

  useEffect(() => {
    if (activeTab === 'moderation') {
      loadModerationItems();
    }
  }, [activeTab, moderationFilter]);

  // Filter moderation items based on search
  const filteredModerationItems = moderationItems.filter(item => {
    const matchesSearch = item.content.toLowerCase().includes(moderationSearch.toLowerCase()) ||
                         item.authorName?.toLowerCase().includes(moderationSearch.toLowerCase());
    return matchesSearch;
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-7xl h-screen flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-2 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Event Management</h2>
            <p className="text-gray-600">{book.botName}</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/80 rounded-xl transition-colors"
            >
              <X className="h-6 w-6 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 border-b-2 font-medium whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-emerald-600 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {tab.count !== undefined && (
                  <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'overview' && (
            <OverviewTab 
              analytics={analytics} 
              book={book} 
              recentBookings={bookings.slice(0, 5)}
              upcomingMilestones={milestones.filter(m => !m.completed).slice(0, 3)}
            />
          )}

          {activeTab === 'guests' && (
            <GuestsTab
              guests={guests}
              bookId={bookId}
              onAdd={() => handleAdd('guest')}
              onEdit={(item) => handleEdit(item, 'guest')}
              onDelete={(id) => handleDelete(id, 'guest')}
              onRefresh={loadAllData}
              loading={loading}
            />
          )}
          
          {activeTab === 'bookings' && (
            <BookingsTab
              bookings={bookings}
              onAdd={() => handleAdd('booking')}
              onEdit={(item) => handleEdit(item, 'booking')}
              onDelete={(id) => handleDelete(id, 'booking')}
              loading={loading}
            />
          )}

          {activeTab === 'tasks' && (
            <EventTasksTab
              tasks={tasks}
              bookId={bookId}
              eventSettings={eventSettings}
              user={user}
              book={book}
              loading={tasksLoading}
              onRefresh={loadTasks}
            />
          )}

          {activeTab === 'messages-updates' && (
            <MessagesUpdatesTab
              messages={messages}
              updates={updates}
              bookId={bookId}
              eventSettings={eventSettings}
              user={user}
              loading={messagesLoading}
              onRefresh={loadMessages}
              selectedItems={selectedMessages}
              setSelectedItems={setSelectedMessages}
              searchQuery={messageSearch}
              setSearchQuery={setMessageSearch}
              filter={messageFilter}
              setFilter={setMessageFilter}
            />
          )}

          {activeTab === 'milestones' && (
            <MilestonesTab
              milestones={milestones}
              onAdd={() => handleAdd('milestone')}
              onEdit={(item) => handleEdit(item, 'milestone')}
              onDelete={(id) => handleDelete(id, 'milestone')}
              onToggleComplete={async (id, completed) => {
                await eventsService.updateMilestone(bookId, id, { completed });
                loadAllData();
              }}
              loading={loading}
            />
          )}
          
          {/* Finances Tab */}
          {activeTab === 'finances' && (
            <div className="p-6">
              <TransactSection 
                bookId={bookId} 
                bookData={book}
              />
            </div>
          )}

          {activeTab === 'gifts' && (
            <GiftsTab
              gifts={gifts}
              onAdd={() => handleAdd('gift')}
              onEdit={(item) => handleEdit(item, 'gift')}
              onDelete={(id) => handleDelete(id, 'gift')}
              loading={loading}
            />
          )}

          {activeTab === 'suppliers' && (
            <SuppliersTab
              suppliers={suppliers}
              onAdd={() => handleAdd('supplier')}
              onEdit={(item) => handleEdit(item, 'supplier')}
              onDelete={(id) => handleDelete(id, 'supplier')}
              loading={loading}
            />
          )}

          {activeTab === 'galleries' && (
            <GalleriesTab
              galleries={galleries}
              bookId={bookId}
              book={book}
              onRefresh={loadAllData}
              loading={loading}
            />
          )}

          {activeTab === 'moderation' && (
                <div className="px-6 space-y-6">
                  {/* Header */}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Content Moderation</h3>
                    <p className="text-gray-600 mt-1">
                      Review and manage messages and updates requiring moderation
                    </p>
                  </div>

                  {/* Stats Cards */}
                  {moderationStats && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                      <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-orange-400">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                            <Clock className="h-6 w-6 text-orange-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-orange-600">{moderationStats.pendingMessages}</p>
                            <p className="text-sm text-gray-600">Pending Messages</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-400">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                            <Bell className="h-6 w-6 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-blue-600">{moderationStats.pendingUpdates}</p>
                            <p className="text-sm text-gray-600">Pending Updates</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-red-400">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                            <Flag className="h-6 w-6 text-red-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-red-600">{moderationStats.reportedMessages}</p>
                            <p className="text-sm text-gray-600">Reported Content</p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-400">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                            <Shield className="h-6 w-6 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-purple-600">{moderationStats.totalPending}</p>
                            <p className="text-sm text-gray-600">Total Pending</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Controls */}
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search content..."
                            value={moderationSearch}
                            onChange={(e) => setModerationSearch(e.target.value)}
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                        </div>
                        
                        <select
                          value={moderationFilter}
                          onChange={(e) => setModerationFilter(e.target.value)}
                          className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="pending">Pending Review</option>
                          <option value="reported">Reported Content</option>
                          <option value="all">All Content</option>
                        </select>
                      </div>

                      {selectedItems.length > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">
                            {selectedItems.length} selected
                          </span>
                          <button
                            onClick={() => handleBulkModerate('approved')}
                            className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1"
                          >
                            <CheckCircle className="h-4 w-4" />
                            Approve All
                          </button>
                          <button
                            onClick={() => handleBulkModerate('rejected')}
                            className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1"
                          >
                            <XCircle className="h-4 w-4" />
                            Reject All
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Content List */}
                    <div className="space-y-4">
                      {moderationLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                        </div>
                      ) : filteredModerationItems.length > 0 ? (
                        filteredModerationItems.map((item) => (
                          <ModerationItemCard
                            key={item.id}
                            item={item}
                            isSelected={selectedItems.includes(item.id)}
                            onSelect={(selected) => {
                              if (selected) {
                                setSelectedItems(prev => [...prev, item.id]);
                              } else {
                                setSelectedItems(prev => prev.filter(id => id !== item.id));
                              }
                            }}
                            onModerate={handleModerate}
                            showActions={moderationFilter === 'pending'}
                          />
                        ))
                      ) : (
                        <div className="text-center py-12">
                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Shield className="h-8 w-8 text-gray-400" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {moderationFilter === 'pending' ? 'No pending content' : 'No content found'}
                          </h3>
                          <p className="text-gray-600">
                            {moderationFilter === 'pending' 
                              ? 'All content has been reviewed.' 
                              : 'Try adjusting your search or filter criteria.'
                            }
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <AddEditModal
          type={modalType}
          item={editingItem}
          bookId={bookId}
          onClose={() => setShowAddModal(false)}
          onSave={() => {
            setShowAddModal(false);
            loadAllData();
            if (onUpdate) onUpdate();
          }}
        />
      )}
    </div>
  );
};

const ModerationItemCard = ({ item, isSelected, onSelect, onModerate, showActions }) => {
  const [showFullContent, setShowFullContent] = useState(false);

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-orange-100 text-orange-700', label: 'Pending Review' },
      approved: { color: 'bg-green-100 text-green-700', label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-700', label: 'Rejected' },
      published: { color: 'bg-blue-100 text-blue-700', label: 'Published' }
    };
    
    const badge = badges[status] || badges.pending;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const badges = {
      message: { icon: MessageSquare, color: 'text-blue-600 bg-blue-100' },
      update: { icon: Bell, color: 'text-green-600 bg-green-100' }
    };
    
    const badge = badges[type] || badges.message;
    const Icon = badge.icon;
    
    return (
      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${badge.color}`}>
        <Icon className="h-3 w-3" />
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </div>
    );
  };

  const truncateContent = (content, maxLength = 150) => {
    if (content.length <= maxLength || showFullContent) return content;
    return content.substring(0, maxLength) + '...';
  };

  const isPending = item.status === 'pending';

  return (
    <div className={`border border-gray-200 rounded-2xl p-6 hover:border-gray-300 transition-colors ${
      isPending ? 'bg-orange-50/30 border-orange-200' : 'bg-white'
    }`}>
      <div className="flex items-start gap-4">
        {showActions && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelect(e.target.checked)}
            className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 mt-1"
          />
        )}
        
        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden">
          {item.authorAvatar ? (
            <img src={item.authorAvatar} alt={item.authorName} className="w-full h-full object-cover" />
          ) : (
            <User className="h-5 w-5 text-gray-400" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-3 mb-3">
            <h4 className="font-medium text-gray-900">
              {item.authorName || 'Anonymous User'}
            </h4>
            {getTypeBadge(item.itemType)}
            {getStatusBadge(item.status)}
            {item.isOrganizer && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-emerald-100 text-emerald-700">
                <Shield className="h-3 w-3 mr-1" />
                Organizer
              </span>
            )}
          </div>

          {/* Content */}
          <div className="mb-4">
            {item.title && (
              <h5 className="font-medium text-gray-900 mb-2">{item.title}</h5>
            )}
            <p className="text-gray-700 leading-relaxed">
              {truncateContent(item.content)}
            </p>
            {item.content.length > 150 && (
              <button
                onClick={() => setShowFullContent(!showFullContent)}
                className="text-purple-600 hover:text-purple-700 text-sm font-medium mt-1"
              >
                {showFullContent ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>

          {/* Metadata */}
          <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {new Date(item.createdAt).toLocaleDateString()} at{' '}
              {new Date(item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
            {item.reports > 0 && (
              <div className="flex items-center gap-1 text-red-600">
                <Flag className="h-4 w-4" />
                {item.reports} report{item.reports !== 1 ? 's' : ''}
              </div>
            )}
          </div>

          {/* Actions */}
          {showActions && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => onModerate(item.id, 'approved', item.itemType)}
                className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Approve
              </button>
              <button
                onClick={() => onModerate(item.id, 'rejected', item.itemType)}
                className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <XCircle className="h-4 w-4" />
                Reject
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ analytics, book, recentBookings, upcomingMilestones }) => {
  const [financialStats, setFinancialStats] = useState(null);

  useEffect(() => {
    // Load financial statsS
    const loadFinancialStats = async () => {
      try {
        const stats = await transactionService.getTransactionAnalytics(book.id);
        setFinancialStats(stats);
      } catch (error) {
        console.error('Error loading financial stats:', error);
      }
    };
    
    loadFinancialStats();
  }, [book.id]);

  return (
  <div className="p-6">
    {/* Analytics Summary */}
            {analytics && (
              <div className="px-6 py-2 border-b bg-gray-50">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  <div className="bg-gray-200 p-3 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-600">{analytics.totalBookings}</p>
                    <p className="text-xs text-gray-600">Bookings</p>
                  </div>
                  <div className="bg-gray-200 p-3 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{analytics.confirmedBookings}</p>
                    <p className="text-xs text-gray-600">Confirmed</p>
                  </div>
                  <div className="bg-gray-200 p-3 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{analytics.totalCheckIns}</p>
                    <p className="text-xs text-gray-600">Check-ins</p>
                  </div>
                  <div className="bg-gray-200 p-3 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{analytics.completedMilestones}/{analytics.totalMilestones}</p>
                    <p className="text-xs text-gray-600">Milestones</p>
                  </div>
                  <div className="bg-gray-200 p-3 rounded-lg">
                    <p className="text-2xl font-bold text-pink-600">{analytics.giftsPurchased}/{analytics.totalGifts}</p>
                    <p className="text-xs text-gray-600">Gifts</p>
                  </div>
                  <div className="bg-gray-200 p-3 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">${analytics.revenue || 0}</p>
                    <p className="text-xs text-gray-600">Revenue</p>
                  </div>
                </div>
              </div>
            )}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Event Info */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4">Event Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600">Event Date</label>
              <p className="font-medium">
                {book.eventSettings?.eventDate ? 
                  new Date(book.eventSettings.eventDate).toLocaleDateString() : 
                  'Not set'
                }
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Location</label>
              <p className="font-medium">{book.eventSettings?.eventLocation || 'Not set'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Max Attendees</label>
              <p className="font-medium">{book.eventSettings?.maxAttendees || 'Unlimited'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Event Type</label>
              <p className="font-medium">{book.eventSettings?.eventType || 'General'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="space-y-4">
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <button className="w-full bg-emerald-50 text-emerald-700 p-3 rounded-lg hover:bg-emerald-100 text-left">
              📧 Send Event Updates
            </button>
            <button className="w-full bg-blue-50 text-blue-700 p-3 rounded-lg hover:bg-blue-100 text-left">
              📊 Export Bookings
            </button>
            <button className="w-full bg-purple-50 text-purple-700 p-3 rounded-lg hover:bg-purple-100 text-left">
              📋 Check-in Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Recent Activity */}
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Bookings</h3>
        {recentBookings.length > 0 ? (
          <div className="space-y-3">
            {recentBookings.map(booking => (
              <div key={booking.id} className="flex justify-between items-center py-2 border-b last:border-b-0">
                <div>
                  <p className="font-medium">{booking.name}</p>
                  <p className="text-sm text-gray-600">{booking.email}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  booking.status === 'confirmed' 
                    ? 'bg-green-100 text-green-700'
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {booking.status}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No bookings yet</p>
        )}
      </div>
      
      {financialStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-green-800">Total Income</h4>
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-green-900">
              R {financialStats.totalIncome.toLocaleString()}
            </p>
            <p className="text-xs text-green-600 mt-1">
              {financialStats.byType.income} transactions
            </p>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-rose-50 p-6 rounded-xl border border-red-200">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-red-800">Total Expenses</h4>
              <DollarSign className="w-5 h-5 text-red-600" />
            </div>
            <p className="text-2xl font-bold text-red-900">
              R {financialStats.totalExpenses.toLocaleString()}
            </p>
            <p className="text-xs text-red-600 mt-1">
              {financialStats.byType.expense} transactions
            </p>
          </div>

          <div className={`bg-gradient-to-br p-6 rounded-xl border ${
            financialStats.netBalance >= 0
              ? 'from-blue-50 to-indigo-50 border-blue-200'
              : 'from-orange-50 to-red-50 border-orange-200'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className={`text-sm font-medium ${
                financialStats.netBalance >= 0 ? 'text-blue-800' : 'text-orange-800'
              }`}>
                Net Balance
              </h4>
              <Wallet className={`w-5 h-5 ${
                financialStats.netBalance >= 0 ? 'text-blue-600' : 'text-orange-600'
              }`} />
            </div>
            <p className={`text-2xl font-bold ${
              financialStats.netBalance >= 0 ? 'text-blue-900' : 'text-orange-900'
            }`}>
              {financialStats.netBalance >= 0 ? '+' : ''}
              R {Math.abs(financialStats.netBalance).toLocaleString()}
            </p>
            <p className={`text-xs mt-1 ${
              financialStats.netBalance >= 0 ? 'text-blue-600' : 'text-orange-600'
            }`}>
              {financialStats.netBalance >= 0 ? 'Surplus' : 'Deficit'}
            </p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">Upcoming Milestones</h3>
        {upcomingMilestones.length > 0 ? (
          <div className="space-y-3">
            {upcomingMilestones.map(milestone => (
              <div key={milestone.id} className="py-2 border-b last:border-b-0">
                <p className="font-medium">{milestone.title}</p>
                <p className="text-sm text-gray-600">
                  Due: {new Date(milestone.targetDate).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No upcoming milestones</p>
        )}
      </div>
    </div>
  </div>
);
};

// Bookings Tab Component
const BookingsTab = ({ bookings, onAdd, onEdit, onDelete, loading }) => (
  <div className="p-6">
    <div className="flex justify-between items-center mb-6">
      <h3 className="text-lg font-semibold">Event Bookings ({bookings.length})</h3>
      <div className="flex gap-2">
        <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center gap-2">
          <Download className="h-4 w-4" />
          Export
        </button>
        <button
          onClick={onAdd}
          className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Booking
        </button>
      </div>
    </div>

    {loading ? (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
      </div>
    ) : (
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attendees</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {bookings.map(booking => (
                <tr key={booking.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium">{booking.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{booking.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{booking.attendeeCount}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      booking.status === 'confirmed' 
                        ? 'bg-green-100 text-green-700'
                        : booking.status === 'pending_payment'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {booking.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    R{booking.amount || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-1">
                      <button
                        onClick={() => onEdit(booking)}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <Edit className="h-4 w-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => onDelete(booking.id)}
                        className="p-1 hover:bg-gray-100 rounded text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
);

// Milestones Tab Component
const MilestonesTab = ({ milestones, onAdd, onEdit, onDelete, onToggleComplete, loading }) => (
  <div className="p-6">
    <div className="flex justify-between items-center mb-6">
      <h3 className="text-lg font-semibold">Event Milestones ({milestones.length})</h3>
      <button
        onClick={onAdd}
        className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 flex items-center gap-2"
      >
        <Plus className="h-4 w-4" />
        Add Milestone
      </button>
    </div>

    {loading ? (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
      </div>
    ) : milestones.length > 0 ? (
      <div className="space-y-4">
        {milestones.map(milestone => {
          const isCompleted = milestone.completed;
          const isOverdue = !isCompleted && new Date(milestone.targetDate) < new Date();
          const daysUntil = Math.ceil((new Date(milestone.targetDate) - new Date()) / (1000 * 60 * 60 * 24));
          
          return (
            <div key={milestone.id} className="bg-white border rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => onToggleComplete(milestone.id, !milestone.completed)}
                    className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      isCompleted 
                        ? 'bg-green-500 border-green-500' 
                        : 'border-gray-300 hover:border-emerald-500'
                    }`}
                  >
                    {isCompleted && <CheckCircle className="w-3 h-3 text-white" />}
                  </button>
                  
                  <div className="flex-1">
                    <h4 className={`font-medium ${isCompleted ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                      {milestone.title}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">{milestone.description}</p>
                    
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <span>Due: {new Date(milestone.targetDate).toLocaleDateString()}</span>
                      {milestone.assignedTo && <span>Assigned to: {milestone.assignedTo}</span>}
                    </div>
                    
                    {isCompleted && milestone.completionNote && (
                      <div className="mt-2 p-2 bg-green-50 rounded text-sm text-green-700">
                        <strong>Completed:</strong> {milestone.completionNote}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {isCompleted ? (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                      Completed
                    </span>
                  ) : isOverdue ? (
                    <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                      Overdue
                    </span>
                  ) : daysUntil <= 7 ? (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                      Due {daysUntil === 0 ? 'today' : `in ${daysUntil} days`}
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      {daysUntil} days left
                    </span>
                  )}
                  
                  <button
                    onClick={() => onEdit(milestone)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <Edit className="h-4 w-4 text-gray-600" />
                  </button>
                  <button
                    onClick={() => onDelete(milestone.id)}
                    className="p-1 hover:bg-gray-100 rounded text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
        <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-sm font-medium text-gray-900 mb-2">No milestones yet</h3>
        <p className="text-sm text-gray-500">Add milestones to track your event planning progress.</p>
      </div>
    )}
  </div>
);

// Gifts Tab Component
const GiftsTab = ({ gifts, onAdd, onEdit, onDelete, loading }) => (
  <div className="p-6">
    <div className="flex justify-between items-center mb-6">
      <h3 className="text-lg font-semibold">Gift Registry ({gifts.length})</h3>
      <button
        onClick={onAdd}
        className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 flex items-center gap-2"
      >
        <Plus className="h-4 w-4" />
        Add Gift Item
      </button>
    </div>

    {loading ? (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
      </div>
    ) : gifts.length > 0 ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {gifts.map(gift => (
          <div key={gift.id} className="bg-white border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
            {gift.image && (
              <div className="aspect-w-16 aspect-h-9">
                <img
                  src={gift.image}
                  alt={gift.name}
                  className="w-full h-32 object-cover"
                />
              </div>
            )}
            
            <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-medium text-gray-900">{gift.name}</h4>
                {gift.priority && (
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    gift.priority === 'high' ? 'bg-red-100 text-red-700' :
                    gift.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {gift.priority}
                  </span>
                )}
              </div>
              
              <p className="text-sm text-gray-600 mb-3">{gift.description}</p>
              
              <div className="flex justify-between items-center mb-3">
                <span className="text-lg font-bold text-emerald-600">R{gift.price}</span>
                {gift.store && (
                  <span className="text-sm text-gray-500">at {gift.store}</span>
                )}
              </div>
              
              {gift.purchased ? (
                <div className="bg-green-50 text-green-700 text-center py-2 rounded text-sm font-medium">
                  ✓ Purchased
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => onEdit(gift)}
                    className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded text-sm hover:bg-gray-200"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onDelete(gift.id)}
                    className="bg-red-100 text-red-700 py-2 px-3 rounded text-sm hover:bg-red-200"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
        <Gift className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-sm font-medium text-gray-900 mb-2">No gift items yet</h3>
        <p className="text-sm text-gray-500">Add items to your gift registry for guests to purchase.</p>
      </div>
    )}
  </div>
);

// Suppliers Tab Component
const SuppliersTab = ({ suppliers, onAdd, onEdit, onDelete, loading }) => (
  <div className="p-6">
    <div className="flex justify-between items-center mb-6">
      <h3 className="text-lg font-semibold">Event Suppliers ({suppliers.length})</h3>
      <button
        onClick={onAdd}
        className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 flex items-center gap-2"
      >
        <Plus className="h-4 w-4" />
        Add Supplier
      </button>
    </div>

    {loading ? (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
      </div>
    ) : suppliers.length > 0 ? (
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Budget</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {suppliers.map(supplier => (
                <tr key={supplier.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="font-medium text-gray-900">{supplier.company}</div>
                      {supplier.website && (
                        <div className="text-sm text-gray-500">{supplier.website}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {supplier.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{supplier.contactName}</div>
                    <div className="text-sm text-gray-500">{supplier.email}</div>
                    <div className="text-sm text-gray-500">{supplier.phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      supplier.status === 'confirmed' 
                        ? 'bg-green-100 text-green-700'
                        : supplier.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-700'
                        : supplier.status === 'contacted'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {supplier.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    R{supplier.estimatedCost || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-1">
                      <button
                        onClick={() => onEdit(supplier)}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <Edit className="h-4 w-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => onDelete(supplier.id)}
                        className="p-1 hover:bg-gray-100 rounded text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ) : (
      <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
        <Truck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-sm font-medium text-gray-900 mb-2">No suppliers yet</h3>
        <p className="text-sm text-gray-500">Add suppliers and vendors for your event planning.</p>
      </div>
    )}
  </div>
);

// Galleries Tab Component
const GalleriesTab = ({ galleries, bookId, book, onRefresh, loading }) => {
  const [showGalleryManager, setShowGalleryManager] = useState(false);
  const [activeGalleryId, setActiveGalleryId] = useState(galleries.length > 0 ? galleries[0].id : 'all');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [sidestripCollapsed, setSidestripCollapsed] = useState(() => {
    return window.innerWidth <= 768; // Collapsed on mobile by default
  });

  // Update active gallery when galleries change
  useEffect(() => {
    if (galleries.length > 0 && !activeGalleryId) {
      setActiveGalleryId(galleries[0].id);
    }
  }, [galleries]);

  const handleMediaClick = (mediaItem) => {
    setSelectedMedia(mediaItem);
    // Could open lightbox or media modal here
  };

  const handleOpenMediaModal = (config) => {
    console.log('Open media modal:', config);
    // Handle media interactions
  };

  const customStyling = {
    primaryColor: book?.customStyling?.primaryColor || '#10b981',
    textColor: book?.customStyling?.textColor || '#1f2937',
    accentColor: book?.customStyling?.accentColor || '#059669'
  };

  const columnsCountBreakPoints = {
    350: 1,
    750: 2,
    900: 3,
    1200: 4
  };

  // Sidestrip settings (can be customized)
  const sidestripSettings = {
    position: 'left',
    showThumbnails: true,
    showLabels: true,
    showCounts: true,
    width: 'medium',
    thumbnailSize: 'medium',
    collapsible: true
  };

  const getStripWidth = (width) => {
    const widthMap = {
      narrow: '64px',   
      medium: '80px',   
      wide: '96px'     
    };
    return widthMap[width] || widthMap.medium;
  };

  // Render individual gallery with LayoutRenderer
  const renderGallery = (gallery) => {
    const mediaItems = gallery?.mediaItems || [];

    if (mediaItems.length === 0) {
      return (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No media in this gallery
          </h3>
          <p className="text-gray-500 mb-6">
            Upload photos and videos to get started
          </p>
          <button
            onClick={() => setShowGalleryManager(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <Upload className="h-4 w-4" />
            Open Gallery Manager
          </button>
        </div>
      );
    }

    return (
      <div className="gallery-content">
        {/* Gallery Info Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h4 className="text-xl font-semibold text-gray-900">
              {gallery.name}
            </h4>
            {gallery.layoutConfig && (
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                {gallery.layoutConfig.layout || 'masonry'}
              </span>
            )}
          </div>
          <span className="text-sm text-gray-500">
            {mediaItems.length} {mediaItems.length === 1 ? 'item' : 'items'}
          </span>
        </div>

        {/* Gallery Render with LayoutRenderer */}
        {gallery.layoutConfig && 
         gallery.layoutConfig.category && 
         gallery.layoutConfig.layout ? (
          <LayoutRenderer
            layoutConfig={gallery.layoutConfig}
            mediaItems={mediaItems}
            galleryId={gallery.id}
            bookId={bookId}
            bookData={book}
            albumSettings={book?.albumSettings}
            customStyling={customStyling}
            responsiveBreakpoints={columnsCountBreakPoints}
            onOpenModal={handleOpenMediaModal}
            onClick={handleMediaClick}
            setSelectedMedia={setSelectedMedia}
            primaryColor={customStyling.primaryColor}
          />
        ) : (
          /* Fallback to masonry if no layout config */
          <ResponsiveMasonry columnsCountBreakPoints={columnsCountBreakPoints}>
            <Masonry gutter="16px">
              {mediaItems.map((item) => (
                <div key={item.id} className="mb-4">
                  <MediaCard
                    mediaItem={item}
                    galleryId={gallery.id}
                    bookId={bookId}
                    bookData={book}
                    albumSettings={book?.albumSettings}
                    customStyling={customStyling}
                    onClick={() => handleMediaClick(item)}
                    primaryColor={customStyling.primaryColor}
                  />
                </div>
              ))}
            </Masonry>
          </ResponsiveMasonry>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <>
      {/* Gallery Sidestrip Navigation */}
      {galleries.length > 0 && (
        <GallerySidestrip
          bookData={book || { favicon: null }}
          galleries={galleries}
          activeGalleryId={activeGalleryId}
          onGalleryChange={setActiveGalleryId}
          settings={sidestripSettings}
          customStyling={customStyling}
          headerHeight={140} // Adjust based on your header height
          isCollapsed={sidestripCollapsed}
          onToggleCollapse={() => setSidestripCollapsed(!sidestripCollapsed)}
        />
      )}

      {/* Main Content */}
      <div 
        className="transition-all duration-300"
        style={{
          marginLeft: galleries.length > 0 && 
                      !sidestripCollapsed && 
                      sidestripSettings.position === 'left'
            ? getStripWidth(sidestripSettings.width)
            : '0',
          marginRight: galleries.length > 0 && 
                      !sidestripCollapsed && 
                      sidestripSettings.position === 'right'
            ? getStripWidth(sidestripSettings.width)
            : '0'
        }}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Event Galleries</h3>
              <p className="text-gray-600 mt-1">
                Photo galleries for your event
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                {galleries.length} {galleries.length === 1 ? 'Gallery' : 'Galleries'}
              </span>
              <button
                onClick={() => setShowGalleryManager(true)}
                className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
              >
                <Settings className="h-4 w-4" />
                Manage Galleries
              </button>
            </div>
          </div>

          {/* Gallery Content */}
          {galleries.length > 0 ? (
            activeGalleryId === 'all' ? (
              // Render all galleries
              <div className="space-y-12">
                {galleries.map((gallery) => (
                  <div key={gallery.id} className="gallery-section">
                    {renderGallery(gallery)}
                  </div>
                ))}
              </div>
            ) : (
              // Render single selected gallery
              (() => {
                const currentGallery = galleries.find(g => g.id === activeGalleryId);
                
                if (!currentGallery) {
                  return (
                    <div className="text-center py-16">
                      <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        Gallery not found
                      </h3>
                      <p className="text-gray-500">
                        The selected gallery could not be loaded
                      </p>
                    </div>
                  );
                }
                
                return renderGallery(currentGallery);
              })()
            )
          ) : (
            <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
              <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No galleries yet</h3>
              <p className="text-gray-500 mb-6">
                Create photo galleries to showcase your event
              </p>
              <button
                onClick={() => setShowGalleryManager(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
              >
                <Plus className="h-5 w-5" />
                Create Gallery
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Gallery Manager Modal */}
      {showGalleryManager && (
        <GalleryManager
          bookId={bookId}
          bookData={book || {}}
          isOpen={showGalleryManager}
          onClose={() => {
            setShowGalleryManager(false);
            onRefresh();
          }}
          onUpdate={onRefresh}
        />
      )}
    </>
  );
};

// Messages & Updates Tab Component
const MessagesUpdatesTab = ({ 
  messages, 
  updates, 
  bookId, 
  eventSettings,
  user,
  loading,
  onRefresh,
  selectedItems,
  setSelectedItems,
  searchQuery,
  setSearchQuery,
  filter,
  setFilter
}) => {
  const [activeSubTab, setActiveSubTab] = useState('all'); // all, messages, updates
  const [updatingVisibility, setUpdatingVisibility] = useState({});

  // Combine and sort messages and updates
  const allItems = [
    ...messages.map(m => ({ ...m, type: 'message' })),
    ...updates.map(u => ({ ...u, type: 'update' }))
  ].sort((a, b) => {
    const dateA = new Date(a.createdAt || a.timestamp);
    const dateB = new Date(b.createdAt || b.timestamp);
    return dateB - dateA;
  });

  // Filter items
  const filteredItems = allItems.filter(item => {
    // Filter by type
    if (activeSubTab === 'messages' && item.type !== 'message') return false;
    if (activeSubTab === 'updates' && item.type !== 'update') return false;

    // Filter by visibility
    if (filter === 'public' && !item.isPublic) return false;
    if (filter === 'hidden' && item.isPublic) return false;

    // Filter by search
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      const text = item.message || item.text || item.content || '';
      const userName = item.userName || item.authorName || '';
      return text.toLowerCase().includes(searchLower) || 
             userName.toLowerCase().includes(searchLower);
    }

    return true;
  });

  const handleToggleVisibility = async (itemId, itemType, currentVisibility) => {
    setUpdatingVisibility(prev => ({ ...prev, [itemId]: true }));
    
    try {
      if (itemType === 'message') {
        await eventsService.updateMessageVisibility(bookId, itemId, !currentVisibility);
      } else {
        await eventsService.updateUpdateVisibility(bookId, itemId, !currentVisibility);
      }
      await onRefresh();
    } catch (error) {
      console.error('Error updating visibility:', error);
      alert('Failed to update visibility');
    } finally {
      setUpdatingVisibility(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const handleBulkVisibility = async (makePublic) => {
    if (selectedItems.length === 0) return;

    try {
      for (const itemId of selectedItems) {
        const item = allItems.find(i => i.id === itemId);
        if (item) {
          if (item.type === 'message') {
            await eventsService.updateMessageVisibility(bookId, itemId, makePublic);
          } else {
            await eventsService.updateUpdateVisibility(bookId, itemId, makePublic);
          }
        }
      }
      setSelectedItems([]);
      await onRefresh();
    } catch (error) {
      console.error('Error bulk updating visibility:', error);
      alert('Failed to update visibility');
    }
  };

  const handleDelete = async (itemId, itemType) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      if (itemType === 'message') {
        await eventsService.deleteMessage(bookId, itemId);
      } else {
        await eventsService.deleteUpdate(bookId, itemId);
      }
      await onRefresh();
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item');
    }
  };

  const toggleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === filteredItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredItems.map(item => item.id));
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-gray-900">Messages & Updates</h3>
          <p className="text-gray-600 mt-1">
            Manage visibility and content for event messages and updates
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-400">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <MessageSquare className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{messages.length}</p>
              <p className="text-sm text-gray-600">Total Messages</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-emerald-400">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Bell className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">{updates.length}</p>
              <p className="text-sm text-gray-600">Total Updates</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-400">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Eye className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">
                {allItems.filter(i => i.isPublic).length}
              </p>
              <p className="text-sm text-gray-600">Public Items</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-gray-400">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
              <EyeOff className="h-6 w-6 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-600">
                {allItems.filter(i => !i.isPublic).length}
              </p>
              <p className="text-sm text-gray-600">Hidden Items</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          {/* Sub tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveSubTab('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeSubTab === 'all'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({allItems.length})
            </button>
            <button
              onClick={() => setActiveSubTab('messages')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeSubTab === 'messages'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Messages ({messages.length})
            </button>
            <button
              onClick={() => setActiveSubTab('updates')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeSubTab === 'updates'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Updates ({updates.length})
            </button>
          </div>

          {/* Search and Filter */}
          <div className="flex gap-2 w-full lg:w-auto">
            <div className="relative flex-1 lg:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">All Items</option>
              <option value="public">Public Only</option>
              <option value="hidden">Hidden Only</option>
            </select>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedItems.length > 0 && (
          <div className="mt-4 flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <span className="text-sm font-medium text-emerald-900">
              {selectedItems.length} selected
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => handleBulkVisibility(true)}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-1"
              >
                <Eye className="h-4 w-4" />
                Make Public
              </button>
              <button
                onClick={() => handleBulkVisibility(false)}
                className="px-3 py-1 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 flex items-center gap-1"
              >
                <EyeOff className="h-4 w-4" />
                Hide
              </button>
              <button
                onClick={() => setSelectedItems([])}
                className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Items List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-sm font-medium text-gray-900 mb-2">No items found</h3>
          <p className="text-sm text-gray-500">
            {searchQuery || filter !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Messages and updates will appear here'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Select All Header */}
          <div className="px-6 py-3 bg-gray-50 border-b flex items-center gap-3">
            <input
              type="checkbox"
              checked={selectedItems.length === filteredItems.length && filteredItems.length > 0}
              onChange={toggleSelectAll}
              className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
            />
            <span className="text-sm font-medium text-gray-700">Select All</span>
          </div>

          {/* Items */}
          <div className="divide-y">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(item.id)}
                    onChange={() => toggleSelectItem(item.id)}
                    className="mt-1 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />

                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    item.type === 'message' 
                      ? 'bg-blue-100' 
                      : 'bg-emerald-100'
                  }`}>
                    {item.type === 'message' ? (
                      <MessageSquare className={`h-5 w-5 ${
                        item.type === 'message' ? 'text-blue-600' : 'text-emerald-600'
                      }`} />
                    ) : (
                      <Bell className="h-5 w-5 text-emerald-600" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            item.type === 'message'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {item.type === 'message' ? 'Message' : 'Update'}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            item.isPublic
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {item.isPublic ? 'Public' : 'Hidden'}
                          </span>
                          {item.status && item.status === 'pending' && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                              Pending Review
                            </span>
                          )}
                        </div>
                        <p className="font-medium text-gray-900">
                          {item.userName || item.authorName || 'Anonymous'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {new Date(item.createdAt || item.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <p className="text-gray-700 whitespace-pre-wrap">
                      {item.message || item.text || item.content}
                    </p>

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-4">
                      <button
                        onClick={() => handleToggleVisibility(item.id, item.type, item.isPublic)}
                        disabled={updatingVisibility[item.id]}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                          item.isPublic
                            ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        } disabled:opacity-50`}
                      >
                        {updatingVisibility[item.id] ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                            <span>Updating...</span>
                          </>
                        ) : item.isPublic ? (
                          <>
                            <EyeOff className="h-4 w-4" />
                            <span>Hide from Public</span>
                          </>
                        ) : (
                          <>
                            <Eye className="h-4 w-4" />
                            <span>Make Public</span>
                          </>
                        )}
                      </button>

                      <button
                        onClick={() => handleDelete(item.id, item.type)}
                        className="px-3 py-1 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors flex items-center gap-1"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Event Tasks Tab Component
const EventTasksTab = ({ 
  tasks, 
  bookId, 
  eventSettings, 
  user, 
  book,
  loading,
  onRefresh 
}) => {
  const [view, setView] = useState('kanban'); // 'kanban' or 'list'
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');
  
  // New toggle states
  const [showStats, setShowStats] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [movingTask, setMovingTask] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    category: 'Event Planning',
    assignedTo: '',
    dueDate: '',
    estimatedHours: '',
    progress: 0,
    tags: ''
  });

  const TASK_STATUS = {
    todo: { label: 'To Do', color: 'gray', icon: Square },
    in_progress: { label: 'In Progress', color: 'blue', icon: PlayCircle },
    on_hold: { label: 'On Hold', color: 'yellow', icon: PauseCircle },
    completed: { label: 'Completed', color: 'green', icon: CheckCircle },
    cancelled: { label: 'Cancelled', color: 'red', icon: XCircle }
  };

  const TASK_PRIORITY = {
    low: { label: 'Low', color: 'green', icon: Flag },
    medium: { label: 'Medium', color: 'yellow', icon: Flag },
    high: { label: 'High', color: 'orange', icon: Flag },
    urgent: { label: 'Urgent', color: 'red', icon: AlertCircle }
  };

  const EVENT_TASK_CATEGORIES = [
    'Event Planning', 'Venue Setup', 'Catering', 'Entertainment', 'Marketing',
    'Registration', 'Logistics', 'Technical', 'Vendors', 'Post-Event', 'Other'
  ];

  // Get status order for navigation
  const STATUS_ORDER = ['todo', 'in_progress', 'on_hold', 'completed', 'cancelled'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Prepare task data WITHOUT manual ID generation
      const taskData = {
        title: formData.title,
        description: formData.description,
        status: formData.status,
        priority: formData.priority,
        category: formData.category,
        assignedTo: formData.assignedTo,
        dueDate: formData.dueDate,
        estimatedHours: formData.estimatedHours ? parseFloat(formData.estimatedHours) : 0,
        progress: parseInt(formData.progress) || 0,
        tags: typeof formData.tags === 'string' 
          ? formData.tags.split(',').map(t => t.trim()).filter(t => t) 
          : formData.tags,
        createdBy: user.uid,
        eventId: bookId
      };

      if (editingTask) {
        // Update existing task - use the REAL Firestore document ID
        await eventsService.updateEventTask(bookId, editingTask.id, taskData);
      } else {
        // Create new task - let Firestore generate the ID
        // DO NOT include an 'id' field in taskData
        await eventsService.createEventTask(bookId, taskData);
      }

      await onRefresh();
      handleCloseForm();
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Failed to save task: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleMoveTask = async (task, direction) => {
    const currentIndex = STATUS_ORDER.indexOf(task.status);
    const newIndex = direction === 'forward' ? currentIndex + 1 : currentIndex - 1;
    
    if (newIndex < 0 || newIndex >= STATUS_ORDER.length) return;
    
    const newStatus = STATUS_ORDER[newIndex];
    const statusLabel = TASK_STATUS[newStatus].label;
    
    // Show confirmation
    const confirmed = window.confirm(
      `Move "${task.title}" to ${statusLabel}?\n\nThis will update the task status and timestamp.`
    );
    
    if (!confirmed) return;

    setMovingTask(task.id);
    
    try {
      await eventsService.updateEventTaskStatus(bookId, task.id, newStatus);
      await onRefresh();
    } catch (error) {
      console.error('Error moving task:', error);
      alert('Failed to move task');
    } finally {
      setMovingTask(null);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await eventsService.updateEventTaskStatus(bookId, taskId, newStatus);
      await onRefresh();
    } catch (error) {
      console.error('Error updating task status:', error);
      alert('Failed to update status');
    }
  };

  const handleProgressUpdate = async (taskId, progress) => {
    try {
      await eventsService.updateEventTaskProgress(bookId, taskId, progress);
      await onRefresh();
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      await eventsService.deleteEventTask(bookId, taskId);
      await onRefresh();
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      ...task,
      tags: Array.isArray(task.tags) ? task.tags.join(', ') : task.tags || ''
    });
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      status: 'todo',
      priority: 'medium',
      category: 'Event Planning',
      assignedTo: user?.email || user?.displayName || '', 
      dueDate: '',
      estimatedHours: '',
      progress: 0,
      tags: ''
    });
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         task.assignedTo?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;
    const matchesAssignee = filterAssignee === 'all' || task.assignedTo === filterAssignee;
    
    return matchesSearch && matchesStatus && matchesPriority && matchesAssignee;
  });

  const getUniqueAssignees = () => {
    return [...new Set(tasks.filter(t => t.assignedTo).map(t => t.assignedTo))];
  };

  const isOverdue = (task) => {
    return task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'completed';
  };

  const getStatusStats = () => {
    return {
      todo: tasks.filter(t => t.status === 'todo').length,
      in_progress: tasks.filter(t => t.status === 'in_progress').length,
      on_hold: tasks.filter(t => t.status === 'on_hold').length,
      completed: tasks.filter(t => t.status === 'completed').length,
      overdue: tasks.filter(isOverdue).length
    };
  };

  const stats = getStatusStats();

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <ClipboardList className="h-7 w-7 text-blue-600" />
            Event Tasks
          </h3>
          <p className="text-gray-600 mt-1">
            Organize and track tasks for {book?.botName || 'your event'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Toggle Buttons */}
          <button
            onClick={() => setShowStats(!showStats)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              showStats
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
            }`}
          >
            <BarChart3 className="h-4 w-4" />
            Stats
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              showFilters
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
            }`}
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>

          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-white border rounded-lg">
            <button
              onClick={() => setView('kanban')}
              className={`px-3 py-2 text-sm font-medium rounded-l-lg transition-colors ${
                view === 'kanban' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Kanban
            </button>
            <button
              onClick={() => setView('list')}
              className={`px-3 py-2 text-sm font-medium rounded-r-lg transition-colors ${
                view === 'list' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              List
            </button>
          </div>

          {/* Add Task Button */}
          <button
            onClick={() => {
              setFormData({
                title: '',
                description: '',
                status: 'todo',
                priority: 'medium',
                category: 'Event Planning',
                assignedTo: user?.email || user?.displayName || '',  // Set current user as default
                dueDate: '',
                estimatedHours: '',
                progress: 0,
                tags: ''
              });
              setShowForm(true);
            }}
            className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 flex items-center gap-2 font-medium"
          >
            <Plus className="h-5 w-5" />
            Add Task
          </button>
        </div>
      </div>

      {/* Stats Cards - Toggleable */}
      {showStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 animate-fadeIn">
          <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-gray-400">
            <div className="flex items-center gap-3">
              <Square className="h-8 w-8 text-gray-600" />
              <div>
                <p className="text-2xl font-bold text-gray-600">{stats.todo}</p>
                <p className="text-sm text-gray-600">To Do</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-blue-400">
            <div className="flex items-center gap-3">
              <PlayCircle className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats.in_progress}</p>
                <p className="text-sm text-gray-600">In Progress</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-yellow-400">
            <div className="flex items-center gap-3">
              <PauseCircle className="h-8 w-8 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold text-yellow-600">{stats.on_hold}</p>
                <p className="text-sm text-gray-600">On Hold</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-green-400">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
                <p className="text-sm text-gray-600">Completed</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-red-400">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-8 w-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
                <p className="text-sm text-gray-600">Overdue</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters - Toggleable */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-sm p-4 animate-fadeIn">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              {Object.entries(TASK_STATUS).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>

            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Priority</option>
              {Object.entries(TASK_PRIORITY).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>

            <select
              value={filterAssignee}
              onChange={(e) => setFilterAssignee(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Assignees</option>
              {getUniqueAssignees().map(assignee => (
                <option key={assignee} value={assignee}>{assignee}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Tasks View */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading tasks...</p>
        </div>
      ) : filteredTasks.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <ClipboardList className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-sm font-medium text-gray-900 mb-2">No tasks found</h3>
          <p className="text-sm text-gray-500 mb-4">
            {searchTerm || filterStatus !== 'all' || filterPriority !== 'all' || filterAssignee !== 'all'
              ? 'Try adjusting your filters'
              : 'Get started by creating your first task'}
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            <Plus className="h-4 w-4" />
            Create Task
          </button>
        </div>
      ) : view === 'kanban' ? (
        <KanbanView
          tasks={filteredTasks}
          TASK_STATUS={TASK_STATUS}
          TASK_PRIORITY={TASK_PRIORITY}
          STATUS_ORDER={STATUS_ORDER}
          onStatusChange={handleStatusChange}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onProgressUpdate={handleProgressUpdate}
          onMoveTask={handleMoveTask}
          movingTask={movingTask}
          isOverdue={isOverdue}
        />
      ) : (
        <ListView
          tasks={filteredTasks}
          TASK_STATUS={TASK_STATUS}
          TASK_PRIORITY={TASK_PRIORITY}
          onStatusChange={handleStatusChange}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onProgressUpdate={handleProgressUpdate}
          isOverdue={isOverdue}
        />
      )}

      {/* Task Form Modal */}
      {showForm && (
      <TaskFormModal
        formData={formData}
        setFormData={setFormData}
        editingTask={editingTask}
        saving={saving}
        onSubmit={handleSubmit}
        onClose={handleCloseForm}
        TASK_STATUS={TASK_STATUS}
        TASK_PRIORITY={TASK_PRIORITY}
        EVENT_TASK_CATEGORIES={EVENT_TASK_CATEGORIES}
        book={book}
        user={user}
        eventSettings={eventSettings}
      />
    )}
    </div>
  );
};

// Kanban View Component
const KanbanView = ({ 
  tasks, 
  TASK_STATUS, 
  TASK_PRIORITY, 
  STATUS_ORDER,
  onStatusChange, 
  onEdit, 
  onDelete, 
  onProgressUpdate, 
  onMoveTask,
  movingTask,
  isOverdue 
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
      {Object.entries(TASK_STATUS).map(([statusKey, { label, color, icon: StatusIcon }]) => {
        const statusTasks = tasks.filter(t => t.status === statusKey);
        const currentIndex = STATUS_ORDER.indexOf(statusKey);
        const canMoveBack = currentIndex > 0;
        const canMoveForward = currentIndex < STATUS_ORDER.length - 1;
        
        return (
          <div key={statusKey} className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <StatusIcon className={`h-5 w-5 text-${color}-600`} />
                <h4 className="font-semibold text-gray-900">{label}</h4>
              </div>
              <span className={`px-2 py-1 bg-${color}-100 text-${color}-700 rounded-full text-xs font-medium`}>
                {statusTasks.length}
              </span>
            </div>

            <div className="space-y-3">
              {statusTasks.map(task => (
                <TaskCard
                  key={task.id}
                  task={task}
                  TASK_PRIORITY={TASK_PRIORITY}
                  TASK_STATUS={TASK_STATUS}
                  canMoveBack={canMoveBack}
                  canMoveForward={canMoveForward}
                  onStatusChange={onStatusChange}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onProgressUpdate={onProgressUpdate}
                  onMoveTask={onMoveTask}
                  movingTask={movingTask}
                  isOverdue={isOverdue}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// List View Component
const ListView = ({ tasks, TASK_STATUS, TASK_PRIORITY, onStatusChange, onEdit, onDelete, onProgressUpdate, isOverdue }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {tasks.map(task => (
              <tr key={task.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium text-gray-900">{task.title}</p>
                    {task.description && (
                      <p className="text-sm text-gray-500 line-clamp-1">{task.description}</p>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <select
                    value={task.status}
                    onChange={(e) => onStatusChange(task.id, e.target.value)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border-0 bg-${TASK_STATUS[task.status].color}-100 text-${TASK_STATUS[task.status].color}-700`}
                  >
                    {Object.entries(TASK_STATUS).map(([key, { label }]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-${TASK_PRIORITY[task.priority].color}-100 text-${TASK_PRIORITY[task.priority].color}-700`}>
                    <Flag className="h-3 w-3" />
                    {TASK_PRIORITY[task.priority].label}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-900">{task.assignedTo || '-'}</span>
                </td>
                <td className="px-6 py-4">
                  {task.dueDate ? (
                    <span className={`text-sm ${isOverdue(task) ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                      {new Date(task.dueDate).toLocaleDateString()}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-emerald-600 h-2 rounded-full transition-all"
                        style={{ width: `${task.progress || 0}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-600 w-10 text-right">{task.progress || 0}%</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-1">
                    <button
                      onClick={() => onEdit(task)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <Edit className="h-4 w-4 text-gray-600" />
                    </button>
                    <button
                      onClick={() => onDelete(task.id)}
                      className="p-1 hover:bg-red-50 rounded text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Task Card Component (for Kanban view)
const TaskCard = ({ 
  task, 
  TASK_PRIORITY, 
  TASK_STATUS, 
  canMoveBack,
  canMoveForward,
  onStatusChange, 
  onEdit, 
  onDelete, 
  onProgressUpdate, 
  onMoveTask,
  movingTask,
  isOverdue 
}) => {
  const PriorityIcon = TASK_PRIORITY[task.priority].icon;
  const isMoving = movingTask === task.id;
  
  return (
    <div className={`bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-all ${
      isMoving ? 'opacity-50 pointer-events-none' : ''
    }`}>
      {/* Priority and Category */}
      <div className="flex items-center justify-between mb-2">
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-${TASK_PRIORITY[task.priority].color}-100 text-${TASK_PRIORITY[task.priority].color}-700`}>
          <PriorityIcon className="h-3 w-3" />
          {TASK_PRIORITY[task.priority].label}
        </span>
        {task.category && (
          <span className="text-xs text-gray-500">{task.category}</span>
        )}
      </div>

      {/* Title */}
      <h5 className="font-medium text-gray-900 mb-2 line-clamp-2">{task.title}</h5>

      {/* Description */}
      {task.description && (
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{task.description}</p>
      )}

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-500">Progress</span>
          <span className="text-xs font-medium text-gray-700">{task.progress || 0}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className="bg-emerald-600 h-1.5 rounded-full transition-all"
            style={{ width: `${task.progress || 0}%` }}
          />
        </div>
      </div>

      {/* Assigned To */}
      {task.assignedTo && (
        <div className="flex items-center gap-2 mb-3">
          <User className="h-3 w-3 text-gray-400" />
          <span className="text-xs text-gray-600">{task.assignedTo}</span>
        </div>
      )}

      {/* Due Date */}
      {task.dueDate && (
        <div className={`flex items-center gap-2 mb-3 ${isOverdue(task) ? 'text-red-600' : 'text-gray-600'}`}>
          <Calendar className="h-3 w-3" />
          <span className="text-xs">{new Date(task.dueDate).toLocaleDateString()}</span>
          {isOverdue(task) && (
            <span className="text-xs font-medium">(Overdue)</span>
          )}
        </div>
      )}

      {/* Tags */}
      {task.tags && task.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {task.tags.slice(0, 3).map((tag, idx) => (
            <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
              <Tag className="h-2.5 w-2.5" />
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Last Updated */}
      {task.updatedAt && (
        <div className="text-xs text-gray-400 mb-3 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Updated: {new Date(task.updatedAt).toLocaleString()}
        </div>
      )}

      {/* Navigation Arrows */}
      <div className="flex items-center gap-2 mb-3 pb-3 border-b">
        <button
          onClick={() => onMoveTask(task, 'back')}
          disabled={!canMoveBack || isMoving}
          className={`flex-1 px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
            canMoveBack && !isMoving
              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              : 'bg-gray-50 text-gray-400 cursor-not-allowed'
          }`}
          title="Move to previous status"
        >
          <ArrowLeft className="h-3 w-3" />
          Back
        </button>
        <button
          onClick={() => onMoveTask(task, 'forward')}
          disabled={!canMoveForward || isMoving}
          className={`flex-1 px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
            canMoveForward && !isMoving
              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
              : 'bg-gray-50 text-gray-400 cursor-not-allowed'
          }`}
          title="Move to next status"
        >
          Forward
          <ArrowRight className="h-3 w-3" />
        </button>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => onEdit(task)}
          disabled={isMoving}
          className="flex-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-xs font-medium hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
        >
          <Edit className="h-3 w-3" />
          Edit
        </button>
        <button
          onClick={() => onDelete(task.id)}
          disabled={isMoving}
          className="px-3 py-1.5 bg-red-50 text-red-600 rounded text-xs font-medium hover:bg-red-100 transition-colors"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>

      {/* Moving Indicator */}
      {isMoving && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
          <div className="flex items-center gap-2 text-emerald-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
            <span className="text-sm font-medium">Moving...</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Task Form Modal Component
const TaskFormModal = ({ 
  formData, 
  setFormData, 
  editingTask, 
  saving, 
  onSubmit, 
  onClose, 
  TASK_STATUS, 
  TASK_PRIORITY,
  EVENT_TASK_CATEGORIES,
  book,
  user,
  eventSettings
}) => {
  // Get list of authorized users
  const getAuthorizedUsers = () => {
    const users = [];
    
    // Add current user first
    if (user) {
      users.push({
        value: user.email || user.displayName || user.uid,
        label: `${user.displayName || user.email || 'You'} (You)`,
        isCurrentUser: true
      });
    }
    
    // Add event creator/owner
    if (book?.createdBy && book.createdBy !== user?.uid) {
      const ownerEmail = book.ownerEmail || book.createdByEmail || 'Event Owner';
      users.push({
        value: ownerEmail,
        label: `${ownerEmail} (Owner)`,
        isOwner: true
      });
    }
    
    // Add organizers from eventSettings
    const organizers = eventSettings?.organizers || [];
    organizers.forEach(organizer => {
      // Skip if already added (current user or owner)
      const alreadyAdded = users.some(u => 
        u.value === organizer || 
        u.value === organizer.email ||
        organizer === user?.uid ||
        organizer === user?.email
      );
      
      if (!alreadyAdded) {
        // If organizer is an object with name/email
        if (typeof organizer === 'object') {
          users.push({
            value: organizer.email || organizer.id,
            label: `${organizer.name || organizer.email} (Organizer)`,
            isOrganizer: true
          });
        } else {
          // If organizer is just a string (email or uid)
          users.push({
            value: organizer,
            label: `${organizer} (Organizer)`,
            isOrganizer: true
          });
        }
      }
    });
    
    // Remove duplicates based on value
    const uniqueUsers = users.filter((user, index, self) =>
      index === self.findIndex(u => u.value === user.value)
    );
    
    return uniqueUsers;
  };

  const authorizedUsers = getAuthorizedUsers();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={onSubmit}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
            <h3 className="text-xl font-semibold text-gray-900">
              {editingTask ? 'Edit Task' : 'Create New Task'}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form Content */}
          <div className="p-6 space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Task Title *</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                placeholder="Enter task title..."
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 resize-none"
                placeholder="Describe the task in detail..."
              />
            </div>

            {/* Status, Priority, Category */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  {Object.entries(TASK_STATUS).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  {Object.entries(TASK_PRIORITY).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                >
                  {EVENT_TASK_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Assignment and Due Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned To *
                </label>
                {authorizedUsers.length > 0 ? (
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      required
                      value={formData.assignedTo}
                      onChange={(e) => setFormData(prev => ({ ...prev, assignedTo: e.target.value }))}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 appearance-none"
                    >
                      <option value="">Select assignee...</option>
                      {authorizedUsers.map((user, idx) => (
                        <option key={idx} value={user.value}>
                          {user.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      required
                      value={formData.assignedTo}
                      onChange={(e) => setFormData(prev => ({ ...prev, assignedTo: e.target.value }))}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder="Enter email or name"
                    />
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Assign to event organizers or team members
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
            </div>

            {/* Estimated Hours and Progress */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estimated Hours
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Clock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.estimatedHours}
                    onChange={(e) => setFormData(prev => ({ ...prev, estimatedHours: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Progress (0-100%)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={formData.progress}
                    onChange={(e) => setFormData(prev => ({ ...prev, progress: e.target.value }))}
                    className="flex-1"
                  />
                  <span className="text-sm font-medium text-gray-700 w-12 text-right">
                    {formData.progress}%
                  </span>
                </div>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-emerald-600 h-2 rounded-full transition-all"
                    style={{ width: `${formData.progress}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags (comma-separated)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Tag className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  placeholder="venue, catering, urgent"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Add tags to help categorize and filter tasks
              </p>
            </div>

            {/* Assignee Info Box */}
            {authorizedUsers.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900 mb-1">
                      Event Team ({authorizedUsers.length})
                    </h4>
                    <p className="text-xs text-blue-700">
                      Tasks can be assigned to event organizers and team members. 
                      {user && ' You are selected by default for new tasks.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50 sticky bottom-0">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  <span>{editingTask ? 'Update Task' : 'Create Task'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};